import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import type {
  CreateConnectionPayload,
  UpdateConnectionPayload,
} from "@/features/data-hub/model/connectionPayloads";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type { CreateConnectionPayload, UpdateConnectionPayload } from "@/features/data-hub/model/connectionPayloads";
export type { CreateCubeConnectionPayload, CubeAuthType } from "@/features/data-hub/model/cubeConnectionForm";
export type { CreateMysqlConnectionPayload } from "@/features/data-hub/model/mysqlConnectionForm";
export type {
  CreateSnowflakeConnectionPayload,
  SnowflakeAuthMethod,
} from "@/features/data-hub/model/snowflakeConnectionForm";

export type ConnectionSummary = {
  id: number;
  name: string;
  type: string;
  created_at: string;
  updated_at: string;
};

/** `GET /v1/connections/{id}` -- unlike {@link ConnectionSummary}, carries the decrypted
 * `config`. Only fetched to prefill the edit modal; never stored in `connectionsSlice.state`. */
export type ConnectionDetail = ConnectionSummary & {
  config: Record<string, unknown>;
};

export type ConnectionsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  connections: ConnectionSummary[];
};

export const connectionsInitialState: ConnectionsState = {
  status: STATUS.IDLE,
  error: null,
  connections: [],
};

function isConnectionSummary(value: unknown): value is ConnectionSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    typeof row.type === "string" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

function parseConnectionsResponse(data: unknown): ConnectionSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isConnectionSummary)) {
    return null;
  }
  return data;
}

export const createConnection = createAsyncThunk<
  ConnectionSummary,
  CreateConnectionPayload,
  { rejectValue: string }
>("connections/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/connections", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isConnectionSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

function isConnectionTestResponse(value: unknown): value is { ok: true } {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return (value as Record<string, unknown>).ok === true;
}

export const testConnection = createAsyncThunk<
  { ok: true },
  CreateConnectionPayload,
  { rejectValue: string }
>("connections/test", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/connections/test", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isConnectionTestResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

function isConnectionDetail(value: unknown): value is ConnectionDetail {
  if (!isConnectionSummary(value)) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.config === "object" && row.config !== null;
}

export const fetchConnectionDetail = createAsyncThunk<
  ConnectionDetail,
  number,
  { rejectValue: string }
>("connections/fetchDetail", async (connectionId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/connections/${String(connectionId)}`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isConnectionDetail(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const updateConnection = createAsyncThunk<
  ConnectionSummary,
  { connectionId: number; payload: UpdateConnectionPayload },
  { rejectValue: string }
>("connections/update", async ({ connectionId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/connections/${String(connectionId)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isConnectionSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchConnections = createAsyncThunk<
  ConnectionSummary[],
  void,
  { rejectValue: string }
>("connections/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/connections", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const connections = parseConnectionsResponse(data);
    if (!connections) {
      return rejectWithValue("Unexpected response shape");
    }
    return connections;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const deleteConnection = createAsyncThunk<number, number, { rejectValue: string }>(
  "connections/delete",
  async (connectionId, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/connections/${String(connectionId)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      return connectionId;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

const connectionsSlice = createSlice({
  name: "connections",
  initialState: connectionsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchConnections.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchConnections.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.connections = action.payload;
      })
      .addCase(fetchConnections.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.connections = [];
        state.error = null;
      })
      .addCase(updateConnection.fulfilled, (state, action) => {
        const index = state.connections.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) {
          state.connections[index] = action.payload;
        }
      })
      .addCase(deleteConnection.fulfilled, (state, action) => {
        state.connections = state.connections.filter(
          (connection) => connection.id !== action.payload,
        );
      });
  },
});

export const connectionsReducer = connectionsSlice.reducer;
